<template>
  <div
    :key="message.from + message.to + message.time"
    class="chat-message"
    :class="{ local: app.connection.id === message.from, private: message.to && message.from, funneled: message.funneled }"
  >
    <div class="chat-message-header">
      <component
        class="chat-message-header-name"
        :is="app.getUser()?.id === message.to ? 'button' : 'div'"
        @click="
          app.getUser()?.id === message.to
            ? (emit('roomSelected', message.from), (app.currentPrivateChat = message.from))
            : undefined
        "
      >
        <i
          v-if="app.getUser()?.id === message.to"
          class="bx bxs-message-alt-check"
          style="cursor: pointer"
          title="Respond to {{ userNameFrom }}"
        />
        <span>{{ userNameFrom }}</span>
        <template v-if="isPrivateChat">
          <span>➞</span>
          <span>{{ userNameTo }}</span>
        </template>
      </component>
      <div class="chat-message-header-time">
        {{ formatTime(message.time) }}
      </div>
    </div>
    <div class="chat-message-body" v-html="formattedMessage" />
  </div>
</template>

<script setup lang="ts">
import type { ChatMessage } from '../types';
import { getApp } from '../models/App';
import { User } from '../models/User';
import { toRef, computed } from 'vue';

const props = defineProps<{
  message: ChatMessage;
}>();

const emit = defineEmits<{
  (e: 'roomSelected', roomName: string): void;
}>();

const app = getApp();

const userNameFrom = computed(() => getUserName(props.message.from));
const userNameTo = computed(() => getUserName(props.message.to));

const getUserName = (id: string) => User.get(id)?.name ?? 'Unknown User';

const isPrivateChat = toRef(() => {
  return props.message.to && props.message.from && app.currentPrivateChat !== app.currentChat;
});

const formatTime = (time: number) => {
  const date = new Date(time);
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
};

const formattedMessage = toRef(() => {
  const msg = props.message.msg;

  // This should implement the same logic as the original Chat.vue component
  // You may want to move this to a utility function if it's used elsewhere
  function createTextLinks(text: string) {
    return text.replace(
      /(https?:\/\/[^\s]+)/g,
      '<a target="_blank" style="color:blue;text-decoration:underline" href="$1">$1</a>',
    );
  }

  function escapeHtml(text: string) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  return createTextLinks(escapeHtml(msg));
});
</script>

<style scoped>
div.chat-messages {
  display: flex;
  flex-direction: column;
  overflow: auto;
}

div.chat-messages > div.chat-message {
  display: flex;
  flex-direction: column;
  margin-top: 5px;
  width: 80%;
}

div.chat-messages > div.chat-message.private {
  font-style: italic;
}

div.chat-messages > div.chat-message.private > div.chat-message-header {
  color: var(--color-chat-private-heading);
}

div.chat-messages > div.chat-message.private > div.chat-message-body {
  background: var(--color-chat-private-bg);
}

div.chat-messages > div.chat-message.local {
  align-self: flex-end;
}

div.chat-messages > div.chat-message.local > div.chat-message-header {
  color: var(--color-black);
  text-align: right;
  justify-content: flex-end;
}

div.chat-messages > div.chat-message.local > div.chat-message-body {
  background: var(--color-chat-message-own);
}

div.chat-messages > div.chat-message.funneled {
  font-style: italic;
}

div.chat-messages > div.chat-message.funneled > div.chat-message-header {
  color: var(--color-chat-funneled-heading);
}

div.chat-messages > div.chat-message.funneled > div.chat-message-body {
  background: var(--color-chat-funneled-bg);
  color: white;
}

div.chat-messages > div.chat-message > div.chat-message-header {
  display: flex;
  color: var(--color-chat-message-heading);
}

div.chat-messages > div.chat-message > div.chat-message-header > div.chat-message-header-name {
  font-weight: bold;
  margin-inline-end: 4px;
  display: flex;
  gap: 4px;
}

div.chat-messages > div.chat-message > div.chat-message-body {
  background: white;
  color: var(--color-chat-message);
  border-radius: 5px;
  padding: 5px 10px;
  word-wrap: break-word;
}
</style>
